import React, { useState, useEffect, useCallback, useRef } from 'react';
import { message } from 'antd';
import { FiltersService } from '../../services/FiltersService';
import { DROPDOWN_CODES } from '../../constants/dropdown.constants';
import SimpleDropdown from './SimpleDropdown';

interface IndustryTypeDropdownProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: 'large' | 'middle' | 'small';
  onDropdownLoad?: () => void; // Callback when dropdown options are loaded
}

const IndustryTypeDropdown: React.FC<IndustryTypeDropdownProps> = ({
  value,
  onChange,
  placeholder = "Select Industry Type",
  disabled = false,
  size = 'large',
  onDropdownLoad
}) => {
  const [industryOptions, setIndustryOptions] = useState<{ key: string, label: string }[]>([]);
  const [loading, setLoading] = useState(false);

  const onDropdownLoadRef = useRef(onDropdownLoad);

  useEffect(() => {
    onDropdownLoadRef.current = onDropdownLoad;
  }, [onDropdownLoad]);

  const loadIndustryOptions = useCallback(async () => {
    setLoading(true);
    try {
      const industryTypes = await FiltersService.getDropdownItems(DROPDOWN_CODES.IndustryType);
      setIndustryOptions(industryTypes.map(item => ({
        key: (item.value || '').trim().toUpperCase(),
        label: item.label || ''
      })));
      if (onDropdownLoadRef.current) onDropdownLoadRef.current();
    } catch (error) {
      console.error('Failed to load industry type options:', error);
      message.error('Failed to load industry type options');
      if (onDropdownLoadRef.current) onDropdownLoadRef.current();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadIndustryOptions();
  }, [loadIndustryOptions]);

  const handleIndustryChange = (industryCode: string) => {
    onChange(industryCode);
  };

  // DEBUG LOG for dropdown value debugging
  console.log('IndustryTypeDropdown value:', value, 'options:', industryOptions);

  return (
    <SimpleDropdown
      options={industryOptions}
      value={value}
      onChange={handleIndustryChange}
      placeholder={loading ? "Loading..." : placeholder}
      disabled={disabled || loading}
      size={size}
    />
  );
};

export default IndustryTypeDropdown; 